import { z } from 'zod';

export const HealthStatus = z.enum([
  'pass',
  'missing',
  'fail',
  'unverified',
  'manual',
  'not_applicable',
]);
export type HealthStatus = z.infer<typeof HealthStatus>;
export const HealthWorkload = z.enum(['web', 'worker', 'sync']);
export type HealthWorkload = z.infer<typeof HealthWorkload>;
export const HealthServiceStatus = z.enum(['healthy', 'degraded', 'unhealthy', 'unverified']);
export type HealthServiceStatus = z.infer<typeof HealthServiceStatus>;
const Text = z.string().max(2000);
const Timestamp = z.iso.datetime();

/** Values reach this boundary only after a field-specific safe-value projection. */
export const ConfigurationEntry = z
  .object({
    key: z.string().min(1).max(200),
    label: Text,
    category: Text,
    source: z.enum(['environment', 'system_setting', 'vault_reference']),
    scope: Text.nullable(),
    required: z.boolean(),
    secret: z.boolean(),
    configured: z.boolean(),
    state: z.enum(['configured', 'missing', 'invalid', 'default', 'not_applicable', 'unverified']),
    value: Text.nullable(),
    description: Text,
    action: Text,
  })
  .strict()
  .refine((entry) => !entry.secret || entry.value === null, {
    message: 'Secret values must never be included in diagnostics.',
    path: ['value'],
  });
export type ConfigurationEntry = z.infer<typeof ConfigurationEntry>;

export const SystemDebugReport = z
  .object({
    generatedAt: Timestamp,
    environment: Text,
    version: Text,
    entries: z.array(ConfigurationEntry),
  })
  .strict();
export type SystemDebugReport = z.infer<typeof SystemDebugReport>;

export const SystemHealthCheck = z
  .object({
    id: z.string().min(1).max(200),
    title: Text,
    category: z.enum([
      'identity',
      'database',
      'storage',
      'vault',
      'directory',
      'mail',
      'renderer',
      'worker',
      'ai',
      'teams',
      'speech',
      'configuration',
      'operations',
    ]),
    status: HealthStatus,
    required: z.boolean(),
    detail: Text,
    action: Text,
    checkedAt: Timestamp.nullable(),
    expiresAt: Timestamp.nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    workload: HealthWorkload,
    replica: Text,
  })
  .strict();
export type SystemHealthCheck = z.infer<typeof SystemHealthCheck>;

export const SystemHealthReport = z
  .object({
    generatedAt: Timestamp,
    environment: Text,
    version: Text,
    workload: HealthWorkload,
    replica: Text,
    configurationStatus: z.enum(['configured', 'attention_required', 'unverified']),
    serviceStatus: HealthServiceStatus,
    launchStatus: z.literal('manual_verification_required'),
    checks: z.array(SystemHealthCheck),
    coverage: z.array(Text),
    nextRefreshAt: Timestamp.nullable(),
  })
  .strict();
export type SystemHealthReport = z.infer<typeof SystemHealthReport>;

/** Machine monitoring intentionally has no configuration, identities, or failure detail. */
export const SystemHealthSummary = z
  .object({
    status: HealthServiceStatus,
    checkedAt: Timestamp,
    version: Text,
    checks: z.array(
      z
        .object({
          id: z.string().min(1).max(200),
          status: HealthStatus,
          workload: HealthWorkload,
          checkedAt: Timestamp.nullable(),
          expiresAt: Timestamp.nullable(),
        })
        .strict(),
    ),
    coverage: z.literal('partial'),
  })
  .strict();
export type SystemHealthSummary = z.infer<typeof SystemHealthSummary>;
